import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { inRange } from 'lodash';
import parse from 'html-react-parser';

const getBackgroundColor = (start, end, mainColor, lightColor) => {
    let result = '';

    const underline = `linear-gradient(${mainColor}, ${mainColor}) bottom/100% 3px, ${lightColor}`;

    if (!start && !end) {
        return underline;
    }

    if (start) {
        result += `linear-gradient(to bottom left, transparent 49.5%, ${mainColor} 50%) bottom 3px left 0/4px 4px`;
    }

    if (end) {
        if (result) {
            result += ', ';
        }
        result += `linear-gradient(to bottom right, transparent 49.5%, ${mainColor} 50%) bottom 3px right 0/4px 4px`;
    }

    return `${result}, ${underline}`;
};

const Container = styled.div`
    line-height: 2.25rem;
`;

const SpanEmpty = styled.span`
    position: relative;
    padding: 5px 0;
    ::selection {
        background: var(--gray200); /* WebKit/Blink Browsers */
    }
    ::-moz-selection {
        background: var(--gray200); /* Gecko Browsers */
    }
`;

SpanEmpty.displayName = 'SpanEmpty';

const SpanSelected = styled(SpanEmpty)`
    background: ${(props) => getBackgroundColor(props.isStart, props.isEnd, 'var(--blue500)', 'var(--blue50)')};
    background-repeat: no-repeat;
    cursor: pointer;
`;

SpanSelected.displayName = 'SpanSelected';

const SpanWarning = styled(SpanEmpty)`
    background: ${(props) => getBackgroundColor(props.isStart, props.isEnd, 'var(--color-warning-default)', 'var(--yellow50)')};
    background-repeat: no-repeat;
    cursor: pointer;
`;

SpanWarning.displayName = 'SpanWarning';

const SpanMissing = styled(SpanEmpty)`
    background: ${(props) => getBackgroundColor(props.isStart, props.isEnd, 'var(--gray700)', 'var(--gray50)')};
    background-repeat: no-repeat;
`;

SpanMissing.displayName = 'SpanMissing';

const SpanWrong = styled(SpanEmpty)`
    background: ${(props) => getBackgroundColor(props.isStart, props.isEnd, 'var(--color-error-default)', 'var(--red50)')};
    background-repeat: no-repeat;
`;

SpanWrong.displayName = 'SpanWrong';

const SpanCorrect = styled(SpanEmpty)`
    background: ${(props) => getBackgroundColor(props.isStart, props.isEnd, 'var(--color-success-default)', 'var(--green50)')};
    background-repeat: no-repeat;
`;

const SpanUnselectable = styled.span`
    position: absolute;
    overflow: hidden;
    width: 1px;
    height: 1px;
    padding: 0;
    border: 0;
    margin: 0 -1px -1px 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
    -webkit-user-select: none;
`;

SpanCorrect.displayName = 'SpanCorrect';

export const HighlightType = {
    EMPTY: SpanEmpty,
    SELECTED: SpanSelected,
    WARNING: SpanWarning,
    MISSING: SpanMissing,
    WRONG: SpanWrong,
    CORRECT: SpanCorrect,
};

const TextHighlighter = ({
    disabled = false,
    highlights = [],
    onSelectionChange = () => {},
    onSelectionOutside = () => {},
    onTextClick = () => {},
    question,
}) => {
    useEffect(() => {
        const onSelectionChangeWrapper = () => {
            const selection = document.getSelection();
            if (disabled) {
                return;
            }
            // chiama la funzione solo se selezione testo del text-highligher identificabile dalla presenza del campo "data-position"
            // se viene evidenziato testo esterno non viene chiamata la funzione
            if (
                selection &&
                selection.rangeCount > 0 &&
                selection.getRangeAt(0) &&
                selection?.anchorNode?.parentNode?.dataset?.position &&
                selection?.focusNode?.parentNode?.dataset?.position
            ) {
                const selection = document.getSelection();
                const selectedText = selection.toString();

                let range = selection.getRangeAt(0);
                range = range.cloneRange(); // hack for Chrome
                range.setStart(range.startContainer, 0); // hack for Safari
                const positionXY = range.getBoundingClientRect();

                const startPosition = selection.anchorNode.parentNode.dataset.position;
                const endPosition = selection.focusNode.parentNode.dataset.position;
                const startIndex = Math.min(startPosition, endPosition);
                const endIndex = Math.max(startPosition, endPosition);

                if (startIndex === endIndex && selectedText === '') {
                    //click singolo
                    const isHightlighted = !!highlights.find((selection) => inRange(startIndex, selection.start, selection.end + 1));

                    onTextClick(isHightlighted, positionXY, startIndex);
                } else {
                    //selezione teso
                    const isHightlighted = highlights.some((selection) => selection.start <= startIndex && selection.end >= endIndex);
                    onSelectionChange(isHightlighted, positionXY, startIndex);
                }
            } else {
                onSelectionOutside();
            }
        };

        if (!disabled) {
            document.onselectionchange = onSelectionChangeWrapper;
        }

        return () => {
            if (!disabled) {
                document.onselectionchange = null;
            }
        };
    }, [disabled, highlights, onSelectionChange, onTextClick, onSelectionOutside]);

    const selectSpanType = (selected) => {
        if (selected) return selected.type;

        return SpanEmpty;
    };

    let probe = 0;
    const options = {
        replace: ({ type, data }) => {
            if (type === 'text') {
                return (
                    <>
                        {[...data].map((char) => {
                            const index = probe++;
                            const selected = highlights.find((selection) => inRange(index, selection.start, selection.end + 1));

                            const Span = selectSpanType(selected);

                            return (
                                <Fragment key={index}>
                                    <Span
                                        data-position={index}
                                        isStart={selected && index === selected.start}
                                        isEnd={selected && index === selected.end}
                                    >
                                        {char}
                                    </Span>
                                    {/* questo Span è necessario per risolvere bug di selezione su browser Safari.
                                    Il bug estende la selezione di una parola con il doppio click o tap prolungato su mobile anche all'ultima
                                    parola della riga precedente, introducendo questo Span si evita il problema
                                     */}
                                    {char === ' ' && <SpanUnselectable>&nbsp;</SpanUnselectable>}
                                </Fragment>
                            );
                        })}
                    </>
                );
            }
        },
    };

    return <Container>{parse(question, options)}</Container>;
};

TextHighlighter.propTypes = {
    disabled: PropTypes.bool,
    highlights: PropTypes.array,
    onSelectionChange: PropTypes.func,
    onSelectionOutside: PropTypes.func,
    onTextClick: PropTypes.func,
    question: PropTypes.string.isRequired,
};

export default TextHighlighter;
